import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    index: string
    label: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    className?: string
}

interface AccentProps {
    children: ReactNode
    className?: string
}

// Serif italic emphasis used inside section titles.
export const Accent: FC<AccentProps> = ({ children, className }): ReactNode => {
    return (
        <span className={cn('font-serif font-normal italic tracking-[-0.01em]', className)}>
            {children}
        </span>
    )
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    index,
    label,
    title,
    description,
    align = 'left',
    className
}): ReactNode => {
    const centered = align === 'center'

    return (
        <div className={cn('max-w-3xl', centered && 'mx-auto text-center', className)}>
            <p className={cn('flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground', centered && 'justify-center')}>
                <span className='text-brand'>{index}</span>
                <span className='h-px w-8 bg-border' />
                {label}
            </p>

            <h2 className='mt-5 text-balance font-heading text-[clamp(2.3rem,4.8vw,4.1rem)] font-semibold leading-[1.02] tracking-[-0.04em] text-ink'>
                {title}
            </h2>

            {description && (
                <p className={cn('mt-5 max-w-2xl text-pretty text-lg leading-relaxed text-muted-foreground', centered && 'mx-auto')}>
                    {description}
                </p>
            )}
        </div>
    )
}
